import React, { useMemo } from 'react';
import { GatsbyImage, IGatsbyImageData, getImage } from 'gatsby-plugin-image';
import { useStaticQuery, graphql } from 'gatsby';
import { Props as CommentProps } from '../components/Comments/CardUpdated';

interface CommentsImages {
  david: IGatsbyImageData;
  max: IGatsbyImageData;
  pietro: IGatsbyImageData;
  alex: IGatsbyImageData;
}

export interface Comment {
  comment: string;
  author: string;
  project: {
    name: string;
    href?: string | undefined;
  };
  image: JSX.Element;
}

const comments: Omit<CommentProps, 'image'>[] = [
  {
    comment: `They were a delight to work with. And they delivered the product we wanted.

Stormotion fostered an enjoyable work atmosphere and focused on delivering a bug-free solution.`,
    author: 'David Lesser, CEO',
    project: {
      name: 'Numina',
      href: 'https://numina.team/',
    },
  },
  {
    comment: `They understand what it takes to be a great service provider, prioritizing our success over money.

I think their approach to addressing ambiguity is their biggest strength. It definitely sets them apart from other remote developers.`,
    author: 'Max Scheidlock, Product Manager',
    project: {
      name: 'HUMANOO',
      href: 'https://www.humanoo.com/',
    },
  },
  {
    comment: `They make the whole business work for us, and their improvements are fundamental to our operations.

They’re reliable, honest, and willing to try new things that will help us. We appreciate how flexible and easygoing they are.`,
    author: 'Pietro Saccomani, Founder',
    project: {
      name: 'MobiLoud',
      href: 'https://www.mobiloud.com/',
    },
  },
  {
    comment: `When I was working with Stormotion, I forgot they were an external agency. They put such effort into my product it might as well have been their own.

I’ve never worked with such a client-focused company before.`,
    author: 'Alexander Wolff, CPO',
    project: {
      name: 'Sjut',
      href: 'https://sjut.net/',
    },
  },
];

const useComments = () => {
  const data = useStaticQuery<CommentsImages>(graphql`
    {
      david: file(relativePath: { eq: "david.png" }) {
        childImageSharp {
          gatsbyImageData(
            height: 72
            width: 72
            placeholder: BLURRED
            layout: FIXED
          )
        }
      }
      max: file(relativePath: { eq: "max.png" }) {
        childImageSharp {
          gatsbyImageData(
            height: 72
            width: 72
            placeholder: BLURRED
            layout: FIXED
          )
        }
      }
      pietro: file(relativePath: { eq: "pietro.png" }) {
        childImageSharp {
          gatsbyImageData(
            height: 72
            width: 72
            placeholder: BLURRED
            layout: FIXED
          )
        }
      }
      alex: file(relativePath: { eq: "alex.png" }) {
        childImageSharp {
          gatsbyImageData(
            height: 72
            width: 72
            placeholder: BLURRED
            layout: FIXED
          )
        }
      }
    }
  `);

  const commentsWithImages = useMemo<Comment[]>(
    () =>
      [data.david, data.max, data.pietro, data.alex].map((icon, index) => ({
        image: (
          <GatsbyImage
            image={getImage(icon)!}
            alt={`Stormotion client ${comments[index].author} from ${comments[index].project}`}
          />
        ),
        ...comments[index],
      })),
    [data]
  );
  return commentsWithImages;
};

export default useComments;
